import type React from 'react';
import { useState } from 'react';
import {
    Box,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Typography,
    type SelectChangeEvent,
} from '@mui/material';

interface UnitValue {
    value: number | string;
    unit: string;
}

interface UnitOption {
    label: string;
    value: string;
}

interface UnitInputProps {
    label?: string;
    value?: UnitValue;
    onChange?: (value: UnitValue) => void;
    unitOptions: UnitOption[];
    minValue?: number;
    maxValue?: number;
    required?: boolean;
    disabled?: boolean;
    error?: boolean;
    helperText?: string;
    valueLabel?: string;
    unitLabel?: string;
    valuePlaceholder?: string;
    unitPlaceholder?: string;
    errorText?: string;
}

const UnitInput: React.FC<UnitInputProps> = ({
    label,
    value: externalValue,
    onChange,
    unitOptions,
    minValue = 1,
    maxValue,
    required = false,
    disabled = false,
    error = false,
    helperText,
    valueLabel = 'Value',
    unitLabel = 'Unit',
    valuePlaceholder = 'Enter value',
    unitPlaceholder = 'Select unit',
    errorText,
}) => {
    const isControlled = !!externalValue;
    const [uncontrolledValue, setUncontrolledValue] = useState<UnitValue>({
        value: '',
        unit: '',
    });

    const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value === '' ? 0 : Number(event.target.value);
        const newState = {
            value: newValue,
            unit: isControlled ? externalValue.unit : uncontrolledValue.unit,
        };

        if (!isControlled) setUncontrolledValue(newState);
        onChange?.(newState);
    };

    const handleUnitChange = (event: SelectChangeEvent<string>) => {
        const newUnit = event.target.value;
        const newState = {
            value: isControlled ? externalValue.value : uncontrolledValue.value,
            unit: newUnit,
        };

        if (!isControlled) setUncontrolledValue(newState);
        onChange?.(newState);
    };

    const currentValue = isControlled ? externalValue : uncontrolledValue;

    return (
        <Box sx={{ width: '100%' }}>
            {label && (
                <Typography
                    variant="body2"
                    sx={{
                        mb: 1,
                        color: error ? 'error.main' : 'text.primary',
                        fontWeight: required ? 500 : 400,
                    }}
                >
                    {label}
                    {required && ' *'}
                </Typography>
            )}
            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'flex-start',
                    '& > *': {
                        flex: 1,
                    },
                }}
            >
                <TextField
                    type="number"
                    value={currentValue.value}
                    onChange={handleValueChange}
                    disabled={disabled}
                    error={error}
                    required={required}
                    onKeyDown={(evt) => ["e", "E", "+", "-", "."].includes(evt.key) && evt.preventDefault()}
                    inputProps={{
                        min: minValue,
                        max: maxValue,
                        step: 1,
                    }}
                    label={valueLabel}
                    placeholder={valuePlaceholder}
                    sx={{
                        '& .MuiInputBase-root': {
                            height: 56,
                        },
                    }}
                />
                <FormControl error={error}>
                    <InputLabel id="unit-select-label" error={error}>
                        {unitLabel}
                    </InputLabel>
                    <Select
                        labelId="unit-select-label"
                        value={currentValue.unit}
                        onChange={handleUnitChange}
                        disabled={disabled}
                        error={error}
                        required={required}
                        placeholder={unitPlaceholder}
                        label={unitLabel}
                        displayEmpty
                        sx={{
                            height: 56,
                        }}
                    >
                        {unitOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
            {helperText && (
                <Typography
                    variant="caption"
                    sx={{
                        mt: 0.5,
                        ml: 1,
                        color: error ? 'error.main' : 'text.secondary',
                    }}
                >
                    {helperText}
                </Typography>
            )}
            {errorText && (
                <Typography color="error" variant="caption" sx={{ mt: 0.5, ml: 1 }}>
                    {errorText}
                </Typography>
            )}
        </Box>
    );
};

export default UnitInput;
